import {
  ReactNode, createContext, useCallback, useContext, useMemo, useRef,
  useState, FormEvent, useEffect,
} from "react";
import { Modal } from "./Modal";

type ConfirmOpts = {
  title?: string;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** When true, renders the confirm button in danger styling. */
  danger?: boolean;
};

type PromptOpts = {
  title?: string;
  label?: string;
  placeholder?: string;
  initial?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  multiline?: boolean;
  /** Optional sync validator. Returning a string shows it as an error. */
  validate?: (value: string) => string | null | undefined;
  /** type=password etc — defaults to "text" / "textarea" if multiline. */
  inputType?: string;
};

type DialogCtx = {
  confirm: (opts: ConfirmOpts) => Promise<boolean>;
  prompt: (opts: PromptOpts) => Promise<string | null>;
};

const Ctx = createContext<DialogCtx | null>(null);

type Pending =
  | { kind: "confirm"; opts: ConfirmOpts; resolve: (v: boolean) => void }
  | { kind: "prompt";  opts: PromptOpts;  resolve: (v: string | null) => void };

export function DialogProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<Pending | null>(null);
  // Stable resolver refs so callers can swap out without dropping a pending dialog.
  const close = useCallback(() => setPending(null), []);

  const api = useMemo<DialogCtx>(() => ({
    confirm: (opts) => new Promise<boolean>((resolve) => {
      setPending({ kind: "confirm", opts, resolve });
    }),
    prompt: (opts) => new Promise<string | null>((resolve) => {
      setPending({ kind: "prompt", opts, resolve });
    }),
  }), []);

  return (
    <Ctx.Provider value={api}>
      {children}
      {pending?.kind === "confirm" && (
        <ConfirmDialog
          opts={pending.opts}
          onCancel={() => { pending.resolve(false); close(); }}
          onConfirm={() => { pending.resolve(true); close(); }}
        />
      )}
      {pending?.kind === "prompt" && (
        <PromptDialog
          opts={pending.opts}
          onCancel={() => { pending.resolve(null); close(); }}
          onConfirm={(v) => { pending.resolve(v); close(); }}
        />
      )}
    </Ctx.Provider>
  );
}

export function useDialog(): DialogCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error("useDialog must be used inside <DialogProvider>");
  return v;
}

function ConfirmDialog({
  opts, onCancel, onConfirm,
}: { opts: ConfirmOpts; onCancel: () => void; onConfirm: () => void }) {
  return (
    <Modal
      open
      onClose={onCancel}
      size="sm"
      title={opts.title ?? "Confirm"}
      footer={
        <>
          <button onClick={onCancel}>{opts.cancelLabel ?? "Cancel"}</button>
          <button
            onClick={onConfirm}
            className={opts.danger ? "danger" : "btn-primary"}
            autoFocus
          >
            {opts.confirmLabel ?? "OK"}
          </button>
        </>
      }
    >
      <p className="dialog-message">{opts.message}</p>
    </Modal>
  );
}

function PromptDialog({
  opts, onCancel, onConfirm,
}: { opts: PromptOpts; onCancel: () => void; onConfirm: (v: string) => void }) {
  const [value, setValue] = useState(opts.initial ?? "");
  const [err, setErr] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  useEffect(() => {
    // Defer focus so portal mount finishes first; selects existing text.
    const handle = requestAnimationFrame(() => {
      inputRef.current?.focus();
      if (inputRef.current && "select" in inputRef.current) inputRef.current.select();
    });
    return () => cancelAnimationFrame(handle);
  }, []);

  function submit(e?: FormEvent) {
    e?.preventDefault();
    const trimmed = value;
    if (opts.validate) {
      const msg = opts.validate(trimmed);
      if (msg) { setErr(msg); return; }
    }
    onConfirm(trimmed);
  }

  return (
    <Modal
      open
      onClose={onCancel}
      size="sm"
      title={opts.title ?? "Input"}
      footer={
        <>
          <button onClick={onCancel}>{opts.cancelLabel ?? "Cancel"}</button>
          <button onClick={() => submit()} className="btn-primary">
            {opts.confirmLabel ?? "OK"}
          </button>
        </>
      }
    >
      <form onSubmit={submit} className="dialog-form">
        {opts.label && <label className="dialog-label">{opts.label}</label>}
        {opts.multiline ? (
          <textarea
            ref={(el) => { inputRef.current = el; }}
            value={value}
            onChange={(e) => { setValue(e.target.value); setErr(null); }}
            placeholder={opts.placeholder}
            rows={4}
          />
        ) : (
          <input
            ref={(el) => { inputRef.current = el; }}
            type={opts.inputType ?? "text"}
            value={value}
            onChange={(e) => { setValue(e.target.value); setErr(null); }}
            placeholder={opts.placeholder}
          />
        )}
        {err && <p className="error dialog-error">{err}</p>}
        {/* No explicit submit button — pressing Enter inside the single
            text input triggers the form's onSubmit implicitly. */}
      </form>
    </Modal>
  );
}
